import { TypedDocumentNode, gql } from '@apollo/client';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FlatList } from '@sorare/core/src/atoms/layout/FlatList';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import {
  FOOTBALL_MY_CLUB_SLUG,
  FOOTBALL_MY_CLUB_SLUG_CARDS,
  FOOTBALL_MY_CLUB_SLUG_CHALLENGES,
  FOOTBALL_MY_CLUB_SLUG_COLLECTIONS,
  FOOTBALL_MY_CLUB_SLUG_COLLECTIONS_COLLECTION_CARDS,
  FOOTBALL_MY_CLUB_SLUG_HISTORY,
  FOOTBALL_PLAY_REWARDS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { FlagSet } from '@sorare/core/src/contexts/featureFlags';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import useLifecycle, { LIFECYCLE } from '@sorare/core/src/hooks/useLifecycle';
import { navLabels } from '@sorare/core/src/lib/glossary';

import DeckEditorDialog from '@sorare/marketplace/src/components/decks/DeckEditorDialog';

import { CollectionPreview } from '@sorare/gameplay/src/components/collections/CollectionPreview';
import { RewardsBanner } from '@sorare/gameplay/src/components/rewards/RewardBanner';
import { RewardBoxBanner } from '@sorare/gameplay/src/components/rewards/RewardBoxBanner';

import { Aside } from '@sorare/manager/src/components/profile/Aside';
import { Challenges } from '@sorare/manager/src/components/profile/Challenges';
import { ClubLayout } from '@sorare/manager/src/components/profile/ClubLayout';
import { Collections } from '@sorare/manager/src/components/profile/Collections';
import { Factory } from '@sorare/manager/src/components/profile/Factory';
import { Gallery } from '@sorare/manager/src/components/profile/Gallery';
import { computeCardsToDisplay } from '@sorare/manager/src/components/profile/Gallery/utils';
import { ManagerIDHeader } from '@sorare/manager/src/components/profile/ManagerIDHeader';
import { OtherClubs } from '@sorare/manager/src/components/profile/OtherClubs';
import { Pro } from '@sorare/manager/src/components/profile/Pro';
import { ReferYourFriends } from '@sorare/manager/src/components/profile/ReferYourFriends';
import {
  CampaignWinner,
  RepresentedBy,
} from '@sorare/manager/src/components/profile/RepresentedBy';
import { Rivals } from '@sorare/manager/src/components/profile/Rivals';
import {
  FlexGrowBlock,
  Grid,
  Main,
  NewGrid,
} from '@sorare/manager/src/components/profile/ui';
import { MyClubOnboarding } from 'components/club/MyClubOnboarding';
import { UnclaimedDeliverableItemOrders } from 'components/rewards/UnclaimedDeliverableItemOrders';
import { LeaderboardOverview } from 'components/rivals/ArenaPage/LeaderboardOverview';
import { MySquad } from 'components/rivals/squads/MySquad';

import {
  FootballManagerProfileQuery,
  FootballManagerProfileQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';
import collectionsFootball from './assets/collections-football.png';

const FOOTBALL_MANAGER_PROFILE_QUERY = gql`
  query FootballManagerProfileQuery(
    $user: String!
    $sport: Sport!
    $isLoggedIn: Boolean!
  ) {
    currentUser @include(if: $isLoggedIn) {
      slug
      unclaimedSo5Rewards(sport: FOOTBALL) {
        slug
        ...RewardsBanner_so5Reward
      }
      unopenedProbabilisticBundles(sport: FOOTBALL, first: 100) {
        nodes {
          id
          ...RewardBoxBanner_ProbabilisticBundle
        }
        totalCount
      }
      ...Factory_currentUser
    }
    user(slug: $user) {
      slug
      nickname
      managerProgression(sport: FOOTBALL) {
        id
        ...ManagerIDHeader_managerProgression
        ...Challenges_ManagerProgression
      }
      footballCardCounts {
        ...Gallery_cardCount
      }
      so5TrophiesSummary {
        ...Pro_trophiesSummary
      }
      cardCollections(startedOnly: true, first: 10, sport: FOOTBALL) {
        nodes {
          slug
          userCardCollection(forUserSlug: $user) {
            id
          }
          ...CollectionPreview_cardCollection
        }
        totalCount
      }
      footballCards(first: 15) {
        nodes {
          slug
          ...Gallery_anyCard
          ...ComputeCardsToDisplay_anyCard
          ...MyClubOnboarding_anyCardInterface
        }
      }
      blockchainFootballCards: footballCards(
        first: 15
        rarities: [limited, rare, super_rare, unique]
      ) {
        nodes {
          slug
          ...Gallery_anyCard
          ...ComputeCardsToDisplay_anyCard
        }
      }
      footballUserProfile {
        id
        clubName
        background {
          id
          pictureUrl
        }
        socialPictureFingerprint
        managerProfileSocialPictureUrls {
          ...Gallery_socialPictureDerivative
        }
        gallerySocialPictureUrls {
          ...Gallery_socialPictureDerivative
        }
        highlightedDeck {
          slug
          name
          socialPictureUrls {
            ...Gallery_socialPictureDerivative
          }
          ...Gallery_deck
        }
        ...ManagerIDHeader_userSportProfile
      }
      ...OtherClubs_user
      ...ManagerIDHeader_user
    }
    football {
      rivals {
        id
        manager(userSlug: $user) {
          id
          ...LeaderboardOverview_footballRivalsManager
        }
        ...MySquad_mySquadOverview
      }
    }
  }
  ${ManagerIDHeader.fragments.user}
  ${ManagerIDHeader.fragments.userSportProfile}
  ${ManagerIDHeader.fragments.managerProgression}
  ${CollectionPreview.fragments.cardCollection}
  ${Pro.fragments.trophiesSummary}
  ${RewardsBanner.fragments.so5Reward}
  ${RewardBoxBanner.fragments.ProbabilisticBundle}
  ${Gallery.fragments.cardCount}
  ${Gallery.fragments.deck}
  ${Gallery.fragments.socialPictureDerivative}
  ${Gallery.fragments.anyCard}
  ${OtherClubs.fragments.user}
  ${Challenges.fragments.managerProgression}
  ${LeaderboardOverview.fragments.footballRivalsManager}
  ${computeCardsToDisplay.fragments.anyCard}
  ${Factory.fragments.currentUser}
  ${MySquad.fragments.footballRivalsRoot}
  ${MyClubOnboarding.fragments.anyCardInterface}
` as TypedDocumentNode<
  FootballManagerProfileQuery,
  FootballManagerProfileQueryVariables
>;

const FootballManagerProfilePage = () => {
  const { slug } = useTypedParams<PageParams>();
  const {
    flags: {
      campaignsWinners = [],
      enableFootballMyClubOnboarding = 'out',
      enableMyClubSplit = false,
    },
  }: {
    flags: {
      campaignsWinners?: CampaignWinner[];
    } & FlagSet;
  } = useFeatureFlags();
  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const { formatMessage } = useIntl();
  const { currentUser, blockchainCardsCount } = useCurrentUserContext();
  const galleryScrollRef = useRef<HTMLDivElement>(null);
  const collectionsScrollRef = useRef<HTMLDivElement>(null);
  const { data, loading } = useQuery(FOOTBALL_MANAGER_PROFILE_QUERY, {
    variables: {
      isLoggedIn: !!currentUser,
      sport: Sport.FOOTBALL,
      user: slug,
    },
    skip: !slug,
  });
  const [editingListSlug, setEditingListSlug] = useState<string | null>();
  const { lifecycle } = useLifecycle();

  if (loading || !data?.user) {
    return <LoadingIndicator fullHeight />;
  }

  const isMe = slug === data?.currentUser?.slug;
  const pageHeader = (
    <PageHeader
      text={
        isMe
          ? navLabels.myClub
          : formatMessage(navLabels.userClub, {
              username: data.user.nickname,
            })
      }
    />
  );

  const rewardsBanner = isMe &&
    !!data?.currentUser?.unclaimedSo5Rewards.length && (
      <FlatList>
        <RewardsBanner rewards={data?.currentUser?.unclaimedSo5Rewards || []} />
        <UnclaimedDeliverableItemOrders />
        <RewardBoxBanner
          rewardBoxes={
            data.currentUser?.unopenedProbabilisticBundles.nodes ?? []
          }
          count={
            data.currentUser?.unopenedProbabilisticBundles?.totalCount || 0
          }
        />
      </FlatList>
    );

  const cards = computeCardsToDisplay(
    data.user.footballCards.nodes,
    data.user.blockchainFootballCards.nodes
  );

  const shareProps = {
    managerProfilePath: generatePath(FOOTBALL_MY_CLUB_SLUG, { slug }),
    managerProfileSocialPictures:
      data?.user.footballUserProfile?.managerProfileSocialPictureUrls,
    galleryPath: generatePath(FOOTBALL_MY_CLUB_SLUG_CARDS, { slug }),
    gallerySocialPictures:
      data?.user.footballUserProfile?.gallerySocialPictureUrls,
    highlightedDeckPath: `${generatePath(FOOTBALL_MY_CLUB_SLUG_CARDS, {
      slug,
    })}?${buildFilterQuery({
      [SEARCH_PARAMS.DECK]:
        data?.user.footballUserProfile?.highlightedDeck?.name,
    })}`,
    highlightedDeckSocialPictures:
      data?.user.footballUserProfile?.highlightedDeck?.socialPictureUrls,
  };

  const footballRivalsManager = data?.football.rivals.manager;
  const campaignWinner = campaignsWinners.find(
    ({ represents }) => represents === data?.user.id
  );

  const blocks = {
    header: (
      <ManagerIDHeader
        user={data.user}
        sport={Sport.FOOTBALL}
        userSportProfile={data.user.footballUserProfile}
        managerProgression={data.user.managerProgression}
        {...shareProps}
      />
    ),
    pro: (
      <Pro
        trophiesSummary={data?.user.so5TrophiesSummary}
        historyUrl={generatePath(FOOTBALL_MY_CLUB_SLUG_HISTORY, { slug })}
        rewardsPoolUrl={FOOTBALL_PLAY_REWARDS}
      />
    ),
    rivals: (
      <Rivals noPadding hideHeader>
        <div>
          <LeaderboardOverview manager={footballRivalsManager} />
          <MySquad rivals={data.football.rivals} />
        </div>
      </Rivals>
    ),
    gallery: (
      <>
        <Gallery
          scrollRef={galleryScrollRef}
          sport={Sport.FOOTBALL}
          cards={cards}
          highlightedDeck={data.user.footballUserProfile?.highlightedDeck}
          galleryUrl={generatePath(FOOTBALL_MY_CLUB_SLUG_CARDS, { slug })}
          onAddDeck={() => setEditingListSlug(null)}
          onEditDeck={deckSlug => setEditingListSlug(deckSlug)}
          cardCount={data.user.footballCardCounts}
          shouldShowOnboarding={
            enableFootballMyClubOnboarding === 'treatment' &&
            blockchainCardsCount > 0 &&
            !lifecycle?.[LIFECYCLE.sawFootballMyClubOnboarding]
          }
          onboardingComponent={
            <MyClubOnboarding
              card={cards.at(0)}
              collectionsScrollRef={collectionsScrollRef}
              galleryScrollRef={galleryScrollRef}
            />
          }
          {...shareProps}
        />
        {editingListSlug !== undefined && (
          <DeckEditorDialog
            sport={Sport.FOOTBALL}
            slug={editingListSlug || ''}
            defaultName="Hall Of Fame"
            onClose={() => setEditingListSlug(undefined)}
          />
        )}
      </>
    ),
    collections: (
      <Collections
        wrapperRef={collectionsScrollRef}
        backgroundImage={collectionsFootball}
        collectionsUrl={generatePath(FOOTBALL_MY_CLUB_SLUG_COLLECTIONS, {
          slug,
        })}
        totalCollections={data.user.cardCollections?.totalCount || 0}
        collections={data.user.cardCollections?.nodes || []}
        renderCollection={collection => (
          <CollectionPreview
            collection={collection}
            to={generatePath(
              FOOTBALL_MY_CLUB_SLUG_COLLECTIONS_COLLECTION_CARDS,
              {
                slug,
                collection: collection.slug,
              }
            )}
          />
        )}
      />
    ),
    otherClubs: <OtherClubs user={data?.user} />,
    refer: <ReferYourFriends />,
    challenges: data.user.managerProgression && (
      <Challenges
        challengesUrl={generatePath(FOOTBALL_MY_CLUB_SLUG_CHALLENGES, { slug })}
        managerProgression={data.user.managerProgression}
        showUnclaimedBadge={isMe}
      />
    ),
    factory: data.currentUser && isMe && (
      <Factory currentUser={data.currentUser} />
    ),
    representedBy: campaignWinner && (
      <RepresentedBy campaignWinner={campaignWinner} />
    ),
  };

  const backgroundUrl = data?.user.footballUserProfile?.background?.pictureUrl;

  if (enableMyClubSplit) {
    if (!isLaptopAndAbove) {
      return (
        <ClubLayout home bannerUrl={backgroundUrl}>
          <Grid>
            {pageHeader}
            {rewardsBanner}
            {blocks.header}
            {blocks.representedBy}
            {blocks.gallery}
            {blocks.rivals}
          </Grid>
        </ClubLayout>
      );
    }
    return (
      <ClubLayout home bannerUrl={backgroundUrl}>
        <NewGrid>
          {pageHeader}
          <Main>
            {rewardsBanner}
            <FlexGrowBlock>{blocks.header}</FlexGrowBlock>
          </Main>
          <Aside>
            <FlexGrowBlock>{blocks.gallery}</FlexGrowBlock>
            {blocks.representedBy}
            {blocks.rivals}
          </Aside>
        </NewGrid>
      </ClubLayout>
    );
  }

  if (!isLaptopAndAbove) {
    return (
      <ClubLayout home bannerUrl={backgroundUrl}>
        <Grid>
          {pageHeader}
          {rewardsBanner}
          {blocks.header}
          {blocks.representedBy}
          {blocks.gallery}
          {blocks.collections}
          {blocks.factory}
          {blocks.rivals}
          {blocks.pro}
          {blocks.challenges}
          {blocks.otherClubs}
          {blocks.refer}
        </Grid>
      </ClubLayout>
    );
  }

  return (
    <ClubLayout home bannerUrl={backgroundUrl}>
      <Grid>
        {pageHeader}
        <Main>
          {rewardsBanner}
          {blocks.header}
          {blocks.factory}
          {blocks.gallery}
          {blocks.collections}
        </Main>
        <Aside>
          {blocks.representedBy}
          {blocks.rivals}
          {blocks.pro}
          {blocks.challenges}
          {blocks.otherClubs}
          {blocks.refer}
        </Aside>
      </Grid>
    </ClubLayout>
  );
};

export default FootballManagerProfilePage;
